import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MapPin, Search, ChevronDown, Heart, MessageCircle } from 'lucide-react';
import { Vendor, fetchVendors, organizeVendorsByType } from '../../types/vendor.ts';
import { useNavigate, useLocation } from 'react-router-dom';
import { FavoritesAPI } from '../../services/api/favoritesApi.ts';

// Import extracted components
import StoryCard from './components/StoryCard.tsx';
import StoryPathNav from './components/VendorNavBar.tsx';
import VendorShowcase from './components/VendorShowcase.tsx';
import VendorCategoryNavigator from './components/VendorCategoryNavigator.tsx';
import SearchBar from './components/SearchBar.tsx';
import ConnectMobile from './ConnectMobile.tsx';

// Wedding journey categories with thoughtful sequencing
const journeyCategories = [
  'Venues',
  'Photography',
  'Decor',
  'Florists',
  'Catering',
  'Entertainment',
  'Transport',
  'Unique',
  'Wedding Planner'
];

// Journey phases for story-based layout
const journeyPhases = [
  { 
    id: 'discover', 
    name: 'Discover', 
    description: 'Find your dream partner',
    color: 'from-rose-400 to-fuchsia-500'
  },
  { 
    id: 'curate', 
    name: 'Curate', 
    description: 'Build your perfect team',
    color: 'from-violet-500 to-purple-600'
  },
  { 
    id: 'connect', 
    name: 'Connect', 
    description: 'Make your vision real',
    color: 'from-blue-500 to-cyan-400'
  }
];

// Theme-based wedding styles for inspiration
const weddingThemes = [
  { id: 'classic', name: 'Classic', image: '/themesOption/Classic.jpg' },
  { id: 'rustic', name: 'Rustic', image: '/themesOption/Rustic.jpg' },
  { id: 'beach', name: 'Beach', image: '/themesOption/Beach.jpg' },
  { id: 'bohemian', name: 'Bohemian', image: '/themesOption/Bohemian.jpg' },
  { id: 'modern', name: 'Modern', image: '/themesOption/Modern.jpg' },
  { id: 'luxury', name: 'Luxury', image: '/themesOption/Luxury.jpg' },
  { id: 'woodland', name: 'Woodland', image: '/themesOption/Woodland.jpg' },
  { id: 'tropical', name: 'Tropical', image: '/themesOption/Tropical.jpg' },
  { id: 'southwestern', name: 'Southwestern', image: '/themesOption/Southwestern.jpg' },
  { id: 'minimalist', name: 'Minimalist', image: '/themesOption/Minrhalistic.jpg' },
  { id: 'invitation', name: 'Invitation', image: '/themesOption/Invitation.jpg' },
  { id: 'cake', name: 'Cake', image: '/themesOption/Cake.jpg' },
  { id: 'mandap', name: 'Mandap', image: '/themesOption/Mandap.jpg' },
];


const Connect: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedCategory, setSelectedCategory] = useState<string>(journeyCategories[0]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [activePhase, setActivePhase] = useState<string>(journeyPhases[0].id);
  const [vendorData, setVendorData] = useState<Record<string, Vendor[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showingFavorites, setShowingFavorites] = useState(false);
  const [favoriteVendors, setFavoriteVendors] = useState<Vendor[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const categoryNavigatorRef = useRef<HTMLDivElement>(null);
  
  // Handle responsive breakpoint
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle URL parameters for category filtering
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    
    if (categoryParam) {
      // Map category parameter to actual category names
      const categoryMapping = {
        'venues': 'Venues',
        'photography': 'Photography', 
        'decor': 'Decor',
        'florists': 'Florists',
        'catering': 'Catering',
        'entertainment': 'Entertainment',
        'transport': 'Transport',
        'unique': 'Unique',
        'planning': 'Wedding Planner'
      };
      
      const mappedCategory = categoryMapping[categoryParam.toLowerCase() as keyof typeof categoryMapping];
      if (mappedCategory && journeyCategories.includes(mappedCategory)) {
        setSelectedCategory(mappedCategory);
      }
    }
  }, [location.search]);

  // Fetch vendors on component mount
  useEffect(() => {
    const getVendors = async () => {
      setIsLoading(true);
      const vendors = await fetchVendors();
      console.log('Raw API Response:', {
        totalVendors: vendors.length,
        vendorTypes: [...new Set(vendors.map(v => v.vendorType))],
        vendorNames: vendors.map(v => v.name),
        worldByPixels: vendors.find(v => v.name === "WorldByPixels")
      });

      const organizedVendors = organizeVendorsByType(vendors);
      console.log('Organized Vendor Data:', {
        categories: Object.keys(organizedVendors),
        vendorsPerCategory: Object.entries(organizedVendors).reduce((acc, [category, vendors]) => {
          acc[category] = {
            count: vendors.length,
            names: vendors.map(v => v.name),
            types: [...new Set(vendors.map(v => v.vendorType))]
          };
          return acc;
        }, {} as Record<string, { count: number; names: string[]; types: string[] }>)
      });

      setVendorData(organizedVendors);
      setIsLoading(false);
    };
    
    getVendors();
  }, []);

  // Add logging for category changes
  useEffect(() => {
    console.log('Category Changed:', {
      selectedCategory,
      vendorsInCategory: vendorData[selectedCategory]?.length || 0,
      vendorNames: vendorData[selectedCategory]?.map(v => v.name) || [],
      worldByPixels: vendorData[selectedCategory]?.find(v => v.name === "WorldByPixels")
    });
  }, [selectedCategory, vendorData]);

  // Featured vendor - select the first verified vendor in the selected category
  const featuredVendor = isLoading 
    ? null 
    : (vendorData[selectedCategory]?.find(v => v.verified) || vendorData[selectedCategory]?.[0] || null);
  
  // Get vendors for showcase - include more vendors and mix verified/top vendors
  const filteredVendors = isLoading
    ? []
    : vendorData[selectedCategory]?.filter(v => {
        // Special logging for worldbypixels
        if (v.name.toLowerCase().includes('worldbypixels')) {
          console.log('Found worldbypixels:', {
            vendor: v,
            category: selectedCategory,
            isFeatured: featuredVendor?.id === v.id,
            searchQuery: searchQuery
          });
        }
        
        // Only filter out the featured vendor if it exists
        if (featuredVendor && v.id === featuredVendor.id) {
          console.log('Filtering out featured vendor:', v.name);
          return false;
        }
        
        // If there's a search query, filter by it
        if (searchQuery) {
          const searchTerms = searchQuery.toLowerCase().split(' ');
          const searchableText = [
            v.name,
            v.vendorType,
            v.specialty,
            v.location,
            v.address,
            v.bio,
            v.locationBio,
            ...v.aboutAttributes.map(attr => 
              `${attr.aboutItemName} ${attr.aboutItemDescription}`
            ),
            ...v.services.map(service => 
              `${service.serviceType} ${service.serviceDescriptions.join(' ')}`
            )
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
            
          // Check if all search terms are found in the searchable text
          const matches = searchTerms.every(term => searchableText.includes(term));
          if (v.name.toLowerCase().includes('worldbypixels')) {
            console.log('worldbypixels search match:', {
              matches,
              searchTerms,
              searchableText
            });
          }
          return matches;
        }
        
        return true;
      })
      .sort((a, b) => {
        // Sort by verified and top vendor status, but don't exclude any vendors
        const aScore = (a.verified ? 2 : 0) + (a.topVendor ? 1 : 0) + (a.rating || 0);
        const bScore = (b.verified ? 2 : 0) + (b.topVendor ? 1 : 0) + (b.rating || 0);
        return bScore - aScore;
      }) || [];

  // Add logging for filtered vendors after they are defined
  useEffect(() => {
    console.log('Filtered Vendors:', {
      selectedCategory,
      totalVendors: vendorData[selectedCategory]?.length || 0,
      filteredCount: filteredVendors.length,
      filteredNames: filteredVendors.map(v => v.name),
      worldByPixels: filteredVendors.find(v => v.name === "WorldByPixels"),
      featuredVendor: featuredVendor?.name
    });
  }, [selectedCategory, vendorData, filteredVendors, featuredVendor]);
  
  // Log final filtered vendors for worldbypixels
  const worldbypixelsInFiltered = filteredVendors.find(v => v.name.toLowerCase().includes('worldbypixels'));
  console.log('worldbypixels in final filtered vendors:', {
    found: !!worldbypixelsInFiltered,
    totalFilteredVendors: filteredVendors.length,
    selectedCategory,
    allVendorsInCategory: vendorData[selectedCategory]?.length
  });
  
  console.log('Selected Category:', selectedCategory);
  console.log('All vendors in category:', vendorData[selectedCategory]?.length);
  console.log('Filtered vendors:', filteredVendors.length);
  console.log('Featured vendor:', featuredVendor?.name);
  
  // Handle vendor selection - redirect to vendor detail page instead of showing modal
  const handleSelectVendor = (vendor: Vendor) => {
    const vendorNameForUrl = vendor.name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
    navigate(`/connect/vendor/${vendor.id}/${vendorNameForUrl}`);
  };
  
  const scrollToCategories = () => {
    categoryNavigatorRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };
  
  // Fetch favorite vendors
  const fetchFavoriteVendors = async () => {
    try {
      setLoadingFavorites(true);
      const favorites = await FavoritesAPI.getFavoriteVendors();
      setFavoriteVendors(favorites);
      setShowingFavorites(true);
    } catch (error) {
      console.error('Error fetching favorite vendors:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (vendor: Vendor) => {
    try {
      if (favoriteVendors.some(v => v.id === vendor.id)) {
        await FavoritesAPI.removeVendorFromFavorites(String(vendor.id));
        setFavoriteVendors(prev => prev.filter(v => v.id !== vendor.id));
      } else {
        await FavoritesAPI.addVendorToFavorites(String(vendor.id));
        setFavoriteVendors(prev => [...prev, vendor]);
      }
    } catch (error) {
      console.error('Failed to update favorite status:', error);
    }
  };
  
  // Handle favorites navigation
  const handleFavoritesClick = () => {
    navigate('/user-profile?tab=favorites');
  };
  const handleInquiriesClick = () => {
    navigate('/user-profile?tab=inquiries');
  };
  
  // Return mobile view if screen width is below threshold
  if (isMobile) {
    return (
      <ConnectMobile
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        vendorData={vendorData}
        isLoading={isLoading}
        handleSelectVendor={handleSelectVendor}
      />
    );
  }

  // Desktop view starts here
  return (
    <div 
      ref={containerRef}
      className="min-h-screen w-full bg-[rgb(255_255_255/var(--tw-bg-opacity,1))] pt-16 overflow-x-hidden"
    >
      {/* Main content area */}
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-16 mb-16 text-center"
        >
          <h1 className="text-5xl md:text-6xl font-medium tracking-tight mb-4">
            <span className="text-transparent bg-clip-text bg-gray-700">
              Wedding Marketplace
            </span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4E8E] to-[#FF7C7C]">
              Driven by 
            </span>
            <span className="text-[#FF4E8E]"> AI</span>
          </h1>
        </motion.div>

        {/* Search and filter bar with favorites button */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <SearchBar 
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              vendors={vendorData}
            />
          </div>
          <div className="bg-white/10 border border-white/20 rounded-2xl p-3 md:p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <button
              onClick={handleInquiriesClick}
              className="flex items-center gap-2 px-4 py-2 rounded-full transition-all bg-[#FF4E8E]/10 text-[#FF4E8E] hover:bg-[#FF4E8E]/20 shadow-lg hover:shadow-xl hover:shadow-[#FF4E8E]/20 border border-[#FF4E8E]/20 hover:border-[#FF4E8E]/30"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="font-medium">Inquiries</span>
            </button>
            <button
              onClick={handleFavoritesClick}
              className="flex items-center gap-2 px-4 py-2 rounded-full transition-all bg-[#5D5FEF]/10 text-[#5D5FEF] hover:bg-[#5D5FEF]/20 shadow-lg hover:shadow-xl hover:shadow-[#5D5FEF]/20 border border-[#5D5FEF]/20 hover:border-[#5D5FEF]/30"
            >
              <Heart className="w-5 h-5" />
              <span className="font-medium">Favorites</span>
            </button>
          </div>
        </div>
        
        {/* Story path navigation - hide when showing favorites */}
        {!showingFavorites && (
          <StoryPathNav 
            categories={journeyCategories}
            selected={selectedCategory}
            onSelect={setSelectedCategory}
          />
        )}
        
        {/* Browse All Categories Button - hide when showing favorites */}
        {!showingFavorites && (
          <div className="flex justify-center mt-4">
            <button 
              onClick={scrollToCategories}
              className="group px-8 py-3 bg-gradient-to-r from-[#FF4E8E] to-[#FF6B6B] text-white font-medium transition-all flex items-center gap-2 shadow-lg hover:shadow-xl hover:shadow-[#FF4E8E]/20 rounded-full"
            >
              <span>Browse All Categories</span>
            </button>
          </div>
        )}
        
        {/* Animated arrow underneath the button */}
        <motion.div 
          className="flex justify-center mt-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <motion.div
            animate={{ 
              y: [0, 10, 0],
              opacity: [0.7, 1, 0.7]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              repeatType: "reverse" 
            }}
            className="text-[#FF4E8E]"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M12 5v14"></path>
              <path d="m19 12-7 7-7-7"></path>
            </svg>
          </motion.div>
        </motion.div>
        
        {/* Content sections with AnimatePresence for smooth transitions */}
        <div className="mt-8 space-y-24">
          {/* Favorites section */}
          {showingFavorites && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative"
            >
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-medium text-gray-800">Your Favorite Vendors</h2>
                <button
                  onClick={() => setShowingFavorites(false)}
                  className="text-[#5D5FEF] hover:text-[#FF4E8E] transition-colors"
                >
                  Back to Categories
                </button>
              </div>
              
              {loadingFavorites ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-8 h-8 border-2 border-[#FF4E8E] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : favoriteVendors.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {favoriteVendors.map((vendor) => (
                    <motion.div
                      key={vendor.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100"
                    >
                      <StoryCard 
                        vendor={vendor} 
                        index={0} 
                        featured={false}
                        onSelect={() => handleSelectVendor(vendor)}
                        onFavoriteToggle={() => handleFavoriteToggle(vendor)}
                        isFavorite={true}
                      />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 bg-white/60 backdrop-blur-md rounded-xl border border-white/40">
                  <Heart className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-[#5D5FEF]/80 text-center text-lg">No favorite vendors yet. Start adding vendors to your favorites!</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Featured vendor spotlight - hide when showing favorites */}
          {!showingFavorites && featuredVendor && (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <motion.div 
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                whileInView={{ 
                  opacity: 1, 
                  y: 0, 
                  scale: 1,
                  transition: { 
                    type: "spring", 
                    stiffness: 50, 
                    damping: 15,
                    delay: 0.2 
                  }
                }}
                className="absolute -top-14 left-4 md:left-8 z-10"
              >
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ 
                  opacity: 1, 
                  y: 0,
                  transition: { 
                    type: "spring", 
                    stiffness: 60, 
                    damping: 20 
                  }
                }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.3 } 
                }}
                className="bg-white border border-gray-100 rounded-2xl shadow-lg overflow-hidden p-4 md:p-8 relative"
              >
                
                {/* Enhanced decorative elements */}
                <motion.div 
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.2, 0.4, 0.2],
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                  className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-[#FF4E8E]/10 to-[#FF7C7C]/10 rounded-full blur-3xl"
                />
                <motion.div 
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.2, 0.3, 0.2],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: 1
                  }}
                  className="absolute -bottom-24 -left-24 w-48 h-48 bg-gradient-to-br from-[#FF7C7C]/10 to-[#FF4E8E]/10 rounded-full blur-3xl"
                />
                
                {/* Radial glow effect for spotlight */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF4E8E]/5 to-[#FF7C7C]/5 opacity-70" />
                <motion.div
                  animate={{
                    boxShadow: [
                      "0 0 0px rgba(255, 78, 142, 0)",
                      "0 0 30px rgba(255, 78, 142, 0.2)",
                      "0 0 0px rgba(255, 78, 142, 0)"
                    ]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                  className="absolute inset-0 rounded-2xl"
                />
                
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 relative z-10">
                  <div className="lg:col-span-3">
                    <StoryCard 
                      vendor={featuredVendor} 
                      index={0} 
                      featured={true}
                      onSelect={() => handleSelectVendor(featuredVendor)} 
                    />
                  </div>
                  
                  <motion.div 
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ 
                      opacity: 1, 
                      x: 0,
                      transition: { 
                        type: "spring", 
                        stiffness: 50, 
                        damping: 15,
                        delay: 0.3 
                      }
                    }}
                    viewport={{ once: true }}
                    className="lg:col-span-2 flex flex-col justify-center"
                  >
                    <div className="space-y-6">
                      <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ 
                          opacity: 1, 
                          y: 0,
                          transition: { delay: 0.4 }
                        }}
                        viewport={{ once: true }}
                        className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF4E8E] to-[#FF7C7C]"
                      >
                        Spotlight Vendor
                      </motion.h2>
                      
                      <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ 
                          opacity: 1, 
                          y: 0,
                          transition: { delay: 0.5 }
                        }}
                        viewport={{ once: true }}
                        className="text-gray-600 text-lg leading-snug"
                      >
                        {featuredVendor.name} is one of our most beloved {selectedCategory.toLowerCase()}. 
                        <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-[#FF4E8E]/10 text-[#FF4E8E] text-sm rounded-full">
                          Nestled
                        </span>
                        in their craft, they create exceptional {featuredVendor.specialty?.toLowerCase() || 'service'} experiences for couples on their special day.
                      </motion.p>
                      
                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ 
                          opacity: 1, 
                          y: 0,
                          transition: { delay: 0.6 }
                        }}
                        viewport={{ once: true }}
                        className="space-y-3"
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <motion.div 
                              animate={{
                                scale: [1, 1.1, 1],
                                backgroundColor: ["rgba(255, 78, 142, 0.1)", "rgba(255, 78, 142, 0.2)", "rgba(255, 78, 142, 0.1)"]
                              }}
                              transition={{
                                duration: 3,
                                repeat: Infinity,
                                repeatType: "reverse"
                              }}
                              className="w-10 h-10 rounded-full bg-[#FF4E8E]/10 flex items-center justify-center"
                            >
                              <Star className="w-5 h-5 text-[#FF4E8E] fill-[#FF4E8E]" />
                            </motion.div>
                            <span className="text-[#FF4E8E] font-semibold">{featuredVendor.rating ? Number(featuredVendor.rating).toFixed(1) : "5.0"}/5.0</span>
                          </div>
                          
                          <div className="flex ml-1 gap-1">
                            {[1, 2, 3, 4, 5].map((star) => {
                              const rating = featuredVendor.rating || 5;
                              // Full star
                              if (star <= Math.floor(rating)) {
                                return (
                                  <Star 
                                    key={star} 
                                    className="w-4 h-4 text-amber-400 fill-amber-400" 
                                    aria-label={`${star} of 5 stars`}
                                  />
                                );
                              }
                              // Half star
                              else if (star === Math.ceil(rating) && !Number.isInteger(rating)) {
                                return (
                                  <div key={star} className="relative w-4 h-4">
                                    <Star className="absolute w-4 h-4 text-gray-300" />
                                    <div className="absolute w-2 h-4 overflow-hidden">
                                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                                    </div>
                                  </div>
                                );
                              }
                              // Empty star
                              else {
                                return (
                                  <Star 
                                    key={star} 
                                    className="w-4 h-4 text-gray-300" 
                                    aria-label={`${star} of 5 stars`}
                                  />
                                );
                              }
                            })}
                            <span className="text-gray-500 text-sm ml-1">
                              by {Math.floor(Math.random() * 50) + 100} happy couples
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mt-4">
                          <div className="bg-white/70 text-gray-800 text-sm px-3 py-1 rounded-full shadow flex items-center gap-1">
                            <span className="font-medium">Price:</span> {featuredVendor.price}
                          </div>
                          <div className="bg-white/70 text-gray-800 text-sm px-3 py-1 rounded-full shadow flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span>{featuredVendor.location}</span>
                          </div>
                        </div>
                      </motion.div>
                      
                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ 
                          opacity: 1, 
                          y: 0,
                          transition: { delay: 0.7 }
                        }}
                        viewport={{ once: true }}
                        whileHover={{ 
                          scale: 1.05,
                          boxShadow: "0px 5px 15px rgba(255, 78, 142, 0.4)"
                        }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-gradient-to-r from-[#FF4E8E] to-[#FF6B6B] text-white font-medium rounded-full px-8 py-3 flex items-center justify-center gap-2 group transition-all shadow-md cursor-pointer"
                        aria-label={`View ${featuredVendor.name}'s story`}
                        onClick={() => handleSelectVendor(featuredVendor)}
                      >
                        View More Details
                        <motion.div
                          animate={{ x: [0, 4, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          className="group-hover:text-white"
                        >
                          →
                        </motion.div>
                      </motion.div>
                      
                      <div className="pt-4 border-t border-gray-100 mt-4">
                        <h4 className="text-sm font-medium text-gray-500 mb-2">Available Services</h4>
                        <div className="flex flex-wrap gap-2">
                          {['Complete Package', 'Customization', 'Coordination', 'Consultation'].map((service) => (
                            <span 
                              key={service}
                              className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          )}
          
          {/* Sliding vendor showcase - hide when showing favorites */}
          {!showingFavorites && (
            <AnimatePresence mode="wait">
              <VendorShowcase 
                vendors={filteredVendors}
                selectedCategory={selectedCategory}
                onSelect={handleSelectVendor}
                onFavoriteToggle={handleFavoriteToggle}
                favoriteVendors={favoriteVendors}
              />
            </AnimatePresence>
          )}
          
          {/* Vertical Category Sidebar - hide when showing favorites */}
          {!showingFavorites && (
            <div className="py-1">
              <div className="relative mb-8">
                <h2 className="text-4xl font-medium text-[#2D3648]">
                  Your Wedding Categories
                </h2>
                <div className="absolute -bottom-3 left-0 right-0 h-1 bg-[#FF4E8E] rounded-full opacity-80"></div>
              </div>
              
              <div ref={categoryNavigatorRef} className="w-full">
                <VendorCategoryNavigator />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Floating journey progression indicator */}
      <div className="fixed bottom-6 right-6 z-40">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1 }}
          className="backdrop-blur-[16px] bg-white/15 border border-white/20 rounded-full shadow-2xl p-1"
        >
        </motion.div>
      </div>
      

    </div>
  );
};

export default Connect; 